import React, { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import {
  useGetInsurerByIdQuery,
  usePatchInsurerByIdMutation,
} from "services/insurer-api";

import { useSelector } from "utils/redux";
import { getInsurerForm } from "forms/insurer";
import { isFetchBaseQueryError } from "utils/error-utils";

import InsurerTPAPageContainer from "./InsurerTPAPageContainer";
import KaboodleForm from "components/Forms/KaboodleForm";

import { setIsLoading } from "reducers/main";
import { useDispatch } from "utils/redux";

const InsurerForm = () => {
  const dispatch = useDispatch();
  const { insurerId } = useParams<{ insurerId?: string }>();

  const loggedInUser = useSelector((state) => state.main.loggedInUser);
  const isReadOnly = !loggedInUser?.has_operations_authority;

  const {
    data: insurer,
    isLoading,
    refetch: refetchInsurer,
  } = useGetInsurerByIdQuery({ id: insurerId as string }, { skip: !insurerId });

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const [patchInsurerById] = usePatchInsurerByIdMutation();

  const { reset, clearErrors, setError } = useFormContext();

  const constants = useSelector((state) => state.shared.constants);

  useEffect(() => {
    if (insurer) {
      reset(insurer);
    }
    refetchInsurer();
  }, [insurer, reset]);

  const handleSave = useCallback(
    async (field: string, value: string | number) => {
      if (isReadOnly) return;
      if (insurerId) {
        try {
          await patchInsurerById({
            id: insurerId,
            data: { [field]: value },
          }).unwrap();
          clearErrors(field);
        } catch (error: unknown) {
          if (isFetchBaseQueryError(error)) {
            const data = error.data as Record<string, string[]> | undefined;
            setError(field, {
              type: String(error.status),
              message: data?.[field]?.[0] || "Unable to save",
            });
          } else {
            setError(field, { message: "Unable to save" });
          }
        }
      }
    },
    [patchInsurerById, insurerId, clearErrors, setError, isReadOnly],
  );

  const insurerFormContent = useMemo(() => {
    const forms = getInsurerForm(constants, isReadOnly);
    return <KaboodleForm cards={forms[0].cards || []} onSave={handleSave} />;
  }, [constants, handleSave, isReadOnly]);

  return (
    <InsurerTPAPageContainer>{insurerFormContent}</InsurerTPAPageContainer>
  );
};

export default InsurerForm;
